import {
  Show,
  SimpleShowLayout,
  TextField,
  NumberField,
  TopToolbar,
  EditButton,
  DeleteButton,
  ReferenceField,
  useRecordContext,
  useGetList,
  useRedirect,
  Button,
  ListButton,
} from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Factory as PlantIcon,
  Security as RoleIcon,
  Info as InfoIcon,
  ViewModule as ViewModuleIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';

const RolePermissionsField = () => {
  const record = useRecordContext();
  console.log('DashboardGroupsShow RolePermissionsField - record:', record);
  console.log('DashboardGroupsShow RolePermissionsField - record.rolePermissions:', record?.rolePermissions);

  if (!record || !record.rolePermissions) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {record.rolePermissions.length > 0 ? (
        record.rolePermissions.map((rp: any, index: number) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={rp.role}
              variant='outlined'
              color='primary'
              icon={<RoleIcon />}
              sx={{
                backgroundColor: 'primary.50',
                borderColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.100',
                },
              }}
            />
            <Chip
              label={rp.permissionLevel}
              variant='filled'
              color='secondary'
              size='small'
              sx={{
                fontWeight: 'medium',
              }}
            />
          </Box>
        ))
      ) : (
        <Typography variant='body2' color='text.secondary' fontStyle='italic'>
          No hay roles asignados
        </Typography>
      )}
    </Box>
  );
};

const ShowTitle = () => {
  const record = useRecordContext();
  return <span>{record ? `Grupo: ${record.groupName}` : 'Cargando...'}</span>;
};

const ShowActions = () => (
  <TopToolbar>
    <EditButton />
    <DeleteButton />
    <ListButton />
  </TopToolbar>
);

const DashboardsList = () => {
  const record = useRecordContext();
  const redirect = useRedirect();

  // Obtener lista de dashboards para este grupo
  const {
    data: dashboards,
    isLoading,
    error,
  } = useGetList('dashboards', {
    filter: { dashboardGroupId: record?.id },
    sort: { field: 'position', order: 'ASC' },
    pagination: { page: 1, perPage: 100 },
  });

  console.log('DashboardsList - record:', record);
  console.log('DashboardsList - dashboards:', dashboards);
  console.log('DashboardsList - isLoading:', isLoading);
  console.log('DashboardsList - error:', error);

  const handleCreateDashboard = () => {
    // Redirigir al formulario de creación con el dashboardGroupId preseleccionado
    redirect(`/dashboards/create?dashboardGroupId=${record?.id}`);
  };

  const handleViewDashboard = (dashboardId: number) => {
    // Redirigir al show del dashboard específico
    redirect(`/dashboards/${dashboardId}/show`);
  };

  if (isLoading) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Cargando dashboards...
      </Typography>
    );
  }

  if (error) {
    return (
      <Typography variant='body2' color='error'>
        Error al cargar dashboards
      </Typography>
    );
  }

  return (
    <Box>
      {/* Botón para crear nuevo dashboard */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          onClick={handleCreateDashboard}
          variant='contained'
          startIcon={<AddIcon />}
          size='small'
          sx={{
            textTransform: 'none',
            fontWeight: 'medium',
          }}
        >
          Crear Dashboard en este Grupo
        </Button>
      </Box>

      {!dashboards || dashboards.length === 0 ? (
        <Paper sx={{ p: 3, bgcolor: 'grey.50', textAlign: 'center' }}>
          <ViewModuleIcon
            sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
          />
          <Typography
            variant='body1'
            color='text.secondary'
            fontWeight='medium'
            gutterBottom
          >
            No hay dashboards asignados a este grupo
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Crea el primer dashboard para comenzar a organizar tu información
          </Typography>
          <Button
            onClick={handleCreateDashboard}
            variant='outlined'
            startIcon={<AddIcon />}
            size='small'
          >
            Crear Primer Dashboard
          </Button>
        </Paper>
      ) : (
        <List
          sx={{
            width: '100%',
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          {dashboards.map((dashboard: any, index: number) => (
            <ListItem
              key={dashboard.id}
              divider={index < dashboards.length - 1}
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
              }}
              secondaryAction={
                <Tooltip title='Ver detalles del dashboard'>
                  <IconButton
                    edge='end'
                    onClick={e => {
                      e.stopPropagation();
                      handleViewDashboard(dashboard.id);
                    }}
                    sx={{
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.50',
                      },
                    }}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
              }
              onClick={() => handleViewDashboard(dashboard.id)}
            >
              <ListItemIcon>
                <ViewModuleIcon color='primary' />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant='subtitle1' fontWeight='medium'>
                      {dashboard.dashboardName}
                    </Typography>
                    <Chip
                      label={`Posición: ${dashboard.position || 'N/A'}`}
                      size='small'
                      variant='outlined'
                      sx={{
                        fontSize: '0.75rem',
                        backgroundColor: 'info.50',
                        borderColor: 'info.main',
                      }}
                    />
                  </Box>
                }
                secondary={
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ mr: 1 }}
                    >
                      Roles de acceso:
                    </Typography>
                    {dashboard.rolePermissions && dashboard.rolePermissions.length > 0 ? (
                      dashboard.rolePermissions.map((rp: any, roleIndex: number) => (
                        <Box key={roleIndex} sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}>
                          <Chip
                            label={rp.role}
                            size='small'
                            color='primary'
                            variant='outlined'
                            sx={{ fontSize: '0.65rem', height: '18px' }}
                          />
                          <Chip
                            label={rp.permissionLevel}
                            size='small'
                            color='secondary'
                            variant='filled'
                            sx={{ fontSize: '0.6rem', height: '16px' }}
                          />
                        </Box>
                      ))
                    ) : (
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        fontStyle='italic'
                      >
                        Sin roles específicos
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

const DashboardGroupsShow = () => {
  return (
    <Show title={<ShowTitle />} actions={<ShowActions />}>
      <Box sx={{ margin: 'auto', padding: 2 }}>
        <Grid container spacing={3}>
          {/* Información General */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: 340 }}>
              <CardContent>
                <Box display='flex' alignItems='center' mb={2}>
                  <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant='h6' component='h2'>
                    Información General
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <SimpleShowLayout>
                  <TextField source='id' label='ID' />
                  <TextField source='groupName' label='Nombre del Grupo' />
                  <NumberField source='position' label='Posición' />
                </SimpleShowLayout>
              </CardContent>
            </Card>
          </Grid>

          {/* Planta Asociada */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: 340 }}>
              <CardContent>
                <Box display='flex' alignItems='center' mb={2}>
                  <PlantIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant='h6' component='h2'>
                    Planta Asociada
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <SimpleShowLayout>
                  <ReferenceField
                    source='plantId'
                    reference='plants'
                    label='Planta'
                    link='show'
                  >
                    <TextField source='plantName' />
                  </ReferenceField>
                  <ReferenceField
                    source='plantId'
                    reference='plants'
                    label='Cliente'
                  >
                    <ReferenceField
                      source='clientId'
                      reference='clients'
                      link='show'
                    >
                      <TextField source='clientName' />
                    </ReferenceField>
                  </ReferenceField>
                </SimpleShowLayout>
              </CardContent>
            </Card>
          </Grid>

          {/* Roles Asignados */}
          <Grid item xs={12}>
            <Card sx={{ height: 340 }}>
              <CardContent>
                <Box display='flex' alignItems='center' mb={2}>
                  <RoleIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant='h6' component='h2'>
                    Roles de Acceso
                  </Typography>
                </Box>{' '}
                <Divider sx={{ mb: 2 }} />
                <RolePermissionsField />
                {/* Mostrar mensaje si no hay roles */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant='body2' color='text.secondary'>
                    Los roles determinan qué usuarios pueden acceder a este
                    grupo de dashboards. Si no hay roles asignados, estará
                    disponible para todos los usuarios.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Dashboards del Grupo */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display='flex' alignItems='center' mb={2}>
                  <ViewModuleIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant='h6' component='h2'>
                    Dashboards del Grupo
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <DashboardsList />
              </CardContent>
            </Card>
          </Grid>

          {/* Información Adicional */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display='flex' alignItems='center' mb={2}>
                  <DashboardIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant='h6' component='h2'>
                    Información del Sistema
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant='body1' gutterBottom>
                  <strong>Estado:</strong> Activo
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Este grupo de dashboards organiza y categoriza los dashboards
                  relacionados con la planta seleccionada. Los usuarios con los
                  roles apropiados podrán acceder y gestionar los dashboards
                  dentro de este grupo.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Show>
  );
};

export default DashboardGroupsShow;
